import { priceCycle } from '../api/utils';
import type {
  ActiveSubscriptions,
  Subscription,
  SubscriptionType,
} from '~/types/plugins/subscriptions';
declare module '#app' {
  interface NuxtApp {
    $subscription: Subscription;
  }
}

export default defineNuxtPlugin({
  async setup() {
    const {
      $auth,
      $i18n,
      $api,
      $store,
      $snackbar,
      $config,
      $login,
      $globals,
      $t,
      $customGtm,
    } = useNuxtApp();

    // state section //

    const subscriptionData: Subscription = reactive({
      subscriptionTypes: [],
      userSubscriptionRaw: [],
      previousUrl: '',
      userWallet: [],
      userWalletFetched: false,
      userSubscribed: false,
      isUserSubscriptionLoaded: false,
      loadingSubTypes: true,
      trialSubscriptions: false as any,
      subscriptionApiFlowResponse: {},
      currentSubscription: {},
      allowedSubTypes: [] as number[],
      inclCancelledSub: false,
      selectedPartnerSubscriptions: [],
      partnerSubscriptionsPlans: [] as any,
      showLoader: false,
      dialogsError: false,
      nextApiFlowStep: null,
      isLoggedIn: computed(() => {
        let user = $auth.user;
        return user?.roles?.[0] === 'user';
      }),
      getRelatedSubscriptionMSS: computed(() => {
        return (subscriptionTypes: []) => {
          const activeSub = [
            ...subscriptionData.userActiveSubscription,
            ...subscriptionData.inActiveSubscriptions,
            ...subscriptionData.pendingSubscriptions,
          ];
          const subgroups: any[] = [];
          subscriptionTypes.forEach((subscriptionType) => {
            const subgroup = activeSub.filter(
              (s) =>
                s &&
                s.subscriptionTypeGroupList &&
                s.subscriptionTypeGroupList.some((g) =>
                  g.levels.some((l) =>
                    l.subscriptionTypeIds.includes(subscriptionType.id),
                  ),
                ),
            );
            if (subgroup && subgroup.length) {
              subgroups.push(subgroup);
            }
          });
          return subgroups.flat();
        };
      }),
      IsGameEligibleForUpgrade: computed(() => {
        return (subscriptionTypes: []) => {
          const activeSub = [
            ...subscriptionData.userActiveSubscription,
            ...subscriptionData.inActiveSubscriptions,
            ...subscriptionData.pendingSubscriptions,
          ];
          let IsGameEligible: any[] = [];
          subscriptionTypes.forEach((id) => {
            IsGameEligible = activeSub.filter(
              (s) =>
                s &&
                s.subscriptionTypeGroupList &&
                s.subscriptionTypeGroupList.some((g) =>
                  g.levels.some((l) => {
                    if (
                      !l?.currentSubscriptionExist &&
                      l?.order > 0 &&
                      l?.subscriptionTypeIds?.includes(id)
                    ) {
                      return true;
                    }
                  }),
                ),
            );
          });
          return !!IsGameEligible?.length;
        };
      }),
      userCreditSubscriptions: computed(() => {
        return subscriptionData.userActiveSubscription?.filter((el: any) =>
          el.contingents?.find((c: any) =>
            c.contentTypes?.find((ct: any) => ct === 'FULL_TRACK_MUSIC'),
          ),
        );
      }),
      checkCreditExpiration: computed(() => {
        return (
          $globals &&
          $globals.enabledExpirationPopup &&
          $globals.enabledExpirationPopup?.showExpirationDialog == 'true' &&
          $auth.user?.roles?.[0] === 'user' &&
          subscriptionData.userCreditSubscriptions?.length > 0
        );
      }),
      userCreditSubscription: computed(() => {
        if (
          subscriptionData.userSubscriptionRaw &&
          subscriptionData.userSubscriptionRaw.length &&
          subscriptionData.subscriptionTypes &&
          subscriptionData.subscriptionTypes.length
        ) {
          return subscriptionData.userSubscriptionRaw.reduce(
            (acc: any, i: any) => {
              if (i.active && i.contingents && Array.isArray(i.contingents)) {
                i.contingents.forEach((c: any) => {
                  c.contentTypes.forEach((ct: any) => {
                    if (!acc[ct]) {
                      acc[ct] = 0;
                    }
                    acc[ct] += c.amount;
                  });
                });
              }
              return acc;
            },
            {},
          );
        }
        return [];
      }),
      userSubscription: computed(() => {
        if (
          subscriptionData.userSubscriptionRaw &&
          subscriptionData.userSubscriptionRaw.length
        ) {
          return subscriptionData.userSubscriptionRaw.map((i: any) => {
            if (i.subscriptionType) {
              i.subscriptionType.priceString = i.subscriptionType;
              i.subscriptionType.billingCycleName = $t(
                priceCycle(i.subscriptionType.billingCycle),
              );
              i.subscriptionType.durationName = $t(
                i.subscriptionType.billingCycle,
              );
              i.subscriptionType.cancelUrl =
                i.subscriptionType?.attributes?.find((category: any) => {
                  return category.key === ('cancelUrl' as String);
                })?.value;
            }
            return i;
          });
        }
        return [];
      }),
      userActiveSubscription: computed(() => {
        return subscriptionData.userSubscription?.filter(
          (el: any) => el.state === 'ACTIVE' && el.subState !== 'PENDING',
        );
      }),
      inActiveSubscriptions: computed(() => {
        return subscriptionData.userSubscription?.filter(
          (el: any) => el.state === 'INACTIVE' && el.subState !== 'PENDING',
        );
      }),
      pendingSubscriptions: computed(() => {
        return subscriptionData.userSubscription?.filter(
          (el: any) =>
            el.state === 'REQUESTED' ||
            (el.subState === 'PENDING' &&
              (el.state === 'ACTIVE' || el.state === 'INACTIVE')),
        );
      }),
      canceledSubscriptions: computed(() => {
        const canceledSubscriptions = [...subscriptionData?.userSubscription];

        canceledSubscriptions.sort((a, b) => (a.id < b.id ? 1 : -1));
        const reduced = canceledSubscriptions.reduce((acc, item) => {
          if (!acc[item.typeId] && item.state === 'CANCELED') {
            acc[item.typeId] = item;
          }
          return acc;
        }, {});
        return Object.values<any>(reduced)
          .sort((a, b) => (a.id < b.id ? 1 : -1))
          .slice(0, 5);
      }),
      hasExpiredSubscriptionWithoutActiveSubscription: computed(() => {
        return (
          subscriptionData.isUserSubsReady &&
          !subscriptionData.userActiveSubscription?.length &&
          !subscriptionData.pendingSubscriptions?.length &&
          subscriptionData.canceledSubscriptions?.length
        );
      }),
      userIsSubscribed: computed(() => {
        return !!subscriptionData.userActiveSubscription?.length;
      }),
      groupedByCategoryAll: computed(() => {
        return subscriptionData.subscriptionTypes.reduce(
          (obj: any, item: any) => {
            const categsAttr: any = item.attributes.find(
              (a: any) => a.key === 'ment_category',
            );
            if (categsAttr) {
              let categroies;
              try {
                categroies = categsAttr?.value && JSON.parse(categsAttr.value);
              } catch (error) {
                categroies = [categsAttr.value];
              }
              const categoryName = categroies.sort().join(',');
              if (!obj[categoryName]) {
                obj[categoryName] = [];
              }
              obj[categoryName].push(item);
            }
            return obj;
          },
          {},
        );
      }),
      groupedByCategory: computed(() => {
        return subscriptionData.subscriptionTypes.reduce(
          (obj: any, item: any) => {
            const categsAttr: any = item.attributes.find(
              (a: any) => a.key === 'ment_category',
            );
            const categroies =
              categsAttr?.value && JSON.parse(categsAttr.value);
            if (categroies?.length > 1) {
              return obj;
            }
            const categoryName = categroies?.sort()?.join(',');
            if (!obj[categoryName]) {
              obj[categoryName] = [];
            }
            obj[categoryName].push(item);
            return obj;
          },
          {},
        );
      }),
      getAllCategories: computed(() => {
        const categoriesLength = Object.keys(
          subscriptionData.groupedByCategory,
        ).length;
        return subscriptionData.subscriptionTypes.reduce(
          (obj: any, item: any) => {
            const categsAttr: any = item.attributes.find(
              (a: any) => a.key === 'ment_category',
            );
            const categroies = JSON.parse(categsAttr.value);
            if (categroies.length !== categoriesLength) {
              return obj;
            }
            const categoryName = categroies.sort().join(',');
            if (!obj[categoryName]) {
              obj[categoryName] = [];
            }
            obj[categoryName].push(item);
            return obj;
          },
          {},
        );
      }),
      getDecideCategories: computed(() => {
        return subscriptionData.subscriptionTypes.reduce(
          (obj: any, item: any) => {
            const categoriesLength = Object.keys(
              subscriptionData.groupedByCategory,
            ).length;
            const categsAttr: any = item.attributes.find(
              (a: any) => a.key === 'ment_category',
            );
            const categroies =
              categsAttr?.value && JSON.parse(categsAttr.value);
            if (
              categroies &&
              (categroies.length === categoriesLength ||
                categroies.length === 1)
            ) {
              return obj;
            }
            const categoryName = categroies?.sort().join(',');
            if (!obj[categoryName]) {
              obj[categoryName] = [];
            }
            obj[categoryName].push(item);
            return obj;
          },
          {},
        );
      }),
      subTypesMap: computed(() => {
        if (subscriptionData.subscriptionTypes) {
          return subscriptionData.subscriptionTypes.reduce(
            (obj: any, item: any) => {
              obj[item.id] = item;
              return obj;
            },
            {},
          );
        }
        return [];
      }),
      subsIsReady: computed(() => {
        return (
          subscriptionData.subscriptionTypes &&
          subscriptionData.subscriptionTypes.length
        );
      }),
      isUserSubsReady: computed(() => {
        return (
          subscriptionData.subsIsReady &&
          subscriptionData.isUserSubscriptionLoaded
        );
      }),
      upgradeToSubscriptions: computed(() => {
        return (subscription: any) => {
          const userSubs = [
            subscriptionData.userActiveSubscription,
            subscriptionData.inActiveSubscriptions,
            subscriptionData.pendingSubscriptions,
          ].flat();
          const group = subscription?.subscriptionTypeGroupList?.[0];
          if (!group) {
            return null;
          }
          let maxOrder = 0;
          let currentLevel: any = null;
          group.levels.forEach((level) => {
            maxOrder = maxOrder < level.order ? level.order : maxOrder;
            if (level.currentSubscriptionExist) {
              currentLevel = level;
            }
          });
          // if (currentLevel?.order < maxOrder) {
          // can be upgraded
          const upgradeSubscriptions = group.levels
            // .filter(i => i.order > currentLevel.order)
            .flatMap((i) => i.subscriptionTypeIds);
          const availableSubTypes = subscriptionData.subscriptionTypes.filter(
            (x) =>
              upgradeSubscriptions.includes(x.id) &&
              x.id != subscription.typeId &&
              !userSubs.some((us) => us.typeId === x.id),
          );
          return availableSubTypes.length && availableSubTypes;
          // const upgradeTo = availableSubTypes.map(i => ({
          //   "toSubscriptionType": i.id,
          //   "fromSubscriptionId": subscription.id
          // }))
          // return upgradeTo;
          // }
        };
      }),
      canRenewSubscription: (subscription: any) => {
        computed(() => {
          const subTypeIds =
            subscription?.subscriptionTypeGroupList
              ?.flatMap((group) => group.levels)
              .flatMap((level) => level.subscriptionTypeIds) || [];
          const userSubsTypeIds = [
            subscriptionData.userActiveSubscription,
            subscriptionData.inActiveSubscriptions,
            subscriptionData.pendingSubscriptions,
          ]
            .flat()
            .map((i) => i.typeId);
          return !subTypeIds.some((s) => userSubsTypeIds.includes(s));
        });
      },
      upgradeToSubscriptionsDirection: computed(() => {
        return (subscription) => {
          const group = subscription?.subscriptionTypeGroupList?.[0];
          if (!group) {
            return null;
          }
          let maxOrder = 0;
          let currentLevel: any = null;
          group.levels.forEach((level) => {
            maxOrder = maxOrder < level.order ? level.order : maxOrder;
            if (level.currentSubscriptionExist) {
              currentLevel = level;
            }
          });
          if (currentLevel.order === 0) {
            return {
              text: 'subscription.upgrade',
              upgradeFlow: upgradeSubscriptionFlow.Upgrade,
            };
          } else if (currentLevel.order === group.levels.length - 1) {
            return {
              text: 'subscription.downgrade',
              upgradeFlow: upgradeSubscriptionFlow.Downgrade,
            };
          } else {
            return {
              text: 'subscription.change',
              upgradeFlow: upgradeSubscriptionFlow.Change,
            };
          }
        };
      }),
      downloadArticle: async (
        item: any,
        startDownload: boolean = true,
      ): Promise<void> => {
        try {
          if (item && item?.id) {
            subscriptionData.showLoader = true;
            const response: any =
              await $api.endPoints.generateSongDownloadLink(item);

            if (response) {
              if (startDownload) {
                const createLink = document.createElement('a');
                createLink.href = response?.uri;
                createLink.setAttribute('download', 'song');
                createLink.click();
              }
              await Promise.all([
                subscriptionData.getUserSubscription(),
                subscriptionData.getUserWallet(),
              ]);
              subscriptionData.showLoader = false;
            } else {
              subscriptionData.dialogsError = response.status;
            }
          }
        } catch (error: any) {
          subscriptionData.dialogsError =
            error?.status || error?.response?.status;
        }
      },
      isFreeTrialEligible: async (): Promise<void> => {
        const user = $auth.user;
        if (user?.roles[0] === 'user') {
          try {
            const response = await $api.endPoints.isFreeTrialEligible();
            subscriptionData.trialSubscriptions = response;
          } catch (error) {}
        }
      },
      getUserWallet: async (): Promise<void> => {
        const user = $auth.user;
        if (user?.roles[0] !== 'user') {
          subscriptionData.userWalletFetched = true;
          return;
        }
        let walletIdsAll: { [key: string]: any }[] = [];
        let offset = 0;
        const limit = 50;
        let xCount = '';
        while (offset === 0 || subscriptionData.hasMore(xCount)) {
          const response: any = await $api.endPoints.getWallet(limit, offset);
          if (!response) {
            return;
          }
          const { walletIds, ...res } = response;
          xCount = res['x-result-count'];
          offset += limit;
          if (walletIds) {
            walletIdsAll.push(...walletIds);
          }
        }
        const userWalletArray: (string | number)[] = [];
        for (const property of walletIdsAll) {
          userWalletArray.push(+property.id);
          if (property.isCollection && property.collectionArticles) {
            userWalletArray.push(...property.collectionArticles);
          }
        }
        subscriptionData.userWallet = userWalletArray;
        subscriptionData.userWalletFetched = true;
      },
      hasMore: (xResultCount: string): boolean => {
        return (
          +xResultCount?.split('/')[0].split('-')[1] <
          +xResultCount?.split('/')[1]
        );
      },
      cancelSubscription: async (
        subscription: any,
        options: any = {},
      ): Promise<unknown> => {
        useNuxtApp().$customGtm.track({
          ET: 'Event',
          EV: $auth?.user?.UUID,
          EA: 'Click to confirm cancellation',
          adobeTrack: {
            adobeET: 'notification',
            adobeData: {
              notification_action: 'displayed',
              notification_message: 'Click to confirm cancellation',
              notification_area: true,
              notification_details: `Click to confirm cancellation ${
                subscription?.name || subscription?.subscriptionType?.name
              }`,
            },
          },
        });
        try {
          if (subscription?.subscriptionType?.cancelUrl) {
            window.location.href = subscription.subscriptionType.cancelUrl;
            return;
          }
          let user = $auth.user;
          const subscriptionId = subscription.id;
          if (user?.roles[0] === 'user' && subscriptionId > 0) {
            const { data } = (await $api.endPoints.cancelSubscription(
              subscriptionId,
              $i18n.locale,
              window.location.href,
            )) as any;
            await subscriptionData.getUserSubscription();
            if (data?.redirectionUrl) {
              useNuxtApp().$customGtm.track({
                EN: 'subscription_cancel',
                AT: 'cancel subscription ',
                PC: 'subscription detail page',
                SI: subscription?.id,
                SN: subscription?.name,
                BN: 'success'
              });
              window.location.href = data?.redirectionUrl;
            } else {
              const text =
                data?.status == '200' || data?.status == '204'
                  ? $t('subscription.cancel.successMsg')
                  : $t('subscription.cancel.failMsg');
              if (!options?.suppressSnackbar) {
                $snackbar.openSnackbar(text);
              }

              if(data?.status == '200' || data?.status == '204') {
                useNuxtApp().$customGtm.track({
                  EN: 'subscription_cancel',
                  AT: 'cancel subscription ',
                  PC: 'subscription detail page',
                  SI: subscription?.id,
                  SN: subscription?.name,
                  SS: 'cancel',
                  BN: 'success',
                  AN: text,
                });
              } else {
                useNuxtApp().$customGtm.track({
                  EN: 'subscription_cancel',
                  AT: 'cancel subscription ',
                  PC: 'subscription detail page',
                  SI: subscription?.id,
                  SN: subscription?.name,
                  BN: 'error'
                });
              }
            }
            return data;
          }
        } catch (error) {
          console.error('Error: cancel subscription');

          useNuxtApp().$customGtm.track({
            EN: 'subscription_cancel',
            AT: 'cancel subscription ',
            PC: 'subscription detail page',
            SI: subscription?.id,
            SN: subscription?.name,
            BN: 'error'
          });

          if (!options?.suppressSnackbar) {
            $snackbar.openSnackbar($t('subscription.cancel.failMsg'));
          }
          return error;
        }
      },
      subscribe: async (
        typeID: number,
        language: string,
        redirectUrl: string,
        articleId: number,
        purchaseType: string,
        migrateSubId: number,
        subscriptionConsentList: any,
        selectedOptions: any,
        selectedUserCard: string | null = null,
        samsungpay: {} | null = null,
      ): Promise<unknown> => {
        try {
          subscriptionData.fireTrackingEvent(typeID);

          if (
            $api.tenant?.default?.mssAutoSubscription &&
            !subscriptionData.isLoggedIn
          ) {
            useNuxtApp().$login.login('', '', '', typeID);
            return;
          }
          if (typeID && language && redirectUrl) {
            // remove message and status from query string
            const url = new URL(redirectUrl);
            url.searchParams.delete('message');
            url.searchParams.delete('status');
            const redirectUrlWithoutQueryString = url.toString();

            let urlQueryParam = redirectUrlWithoutQueryString.includes('?')
              ? redirectUrlWithoutQueryString + '&type=' + typeID
              : redirectUrlWithoutQueryString + '?type=' + typeID;

            if (articleId && purchaseType) {
              urlQueryParam += `&purchaseID=${articleId}&purchaseType=${purchaseType}`;
            }
            const price = subscriptionData.getSubPrice(typeID);
            let upgradeTo = sessionStorage.getItem('upgradeTo');
            if (upgradeTo) {
              upgradeTo = JSON.parse(upgradeTo);
            }
            const req = await $api.endPoints.subscribeUser(
              typeID,
              language,
              urlQueryParam,
              price,
              $auth.user.userId,
              subscriptionConsentList,
              migrateSubId,
              selectedOptions,
              upgradeTo,
              selectedUserCard,
              samsungpay,
            );
            if (
              req?.data?.data?.error === 'REQUIRED_IDENTITY_FIELD_NOT_FOUND'
            ) {
              return req.data.data;
            }
            if (req?.data?.options?.length) {
              if (req?.data?.options.length > 1) {
                const selectedSubcription =
                  subscriptionData.subscriptionTypes.find(
                    (sub) => sub.id == typeID,
                  );
                useNuxtApp().$dialogs.openPaymentOptionsPop(
                  req?.data?.options,
                  selectedSubcription,
                );
                return;
              } else {
                subscriptionData.subscribeToMss(
                  req.data.options[0],
                  typeID,
                  selectedOptions,
                );
                return;
              }
            }
            if (req?.data?.type == 'redirect' && req?.data?.url) {
              return (window.location.href =
                req.data.url + `?lang=${language}`);
            } else if (req?.data?.type == 'onclick') {
              subscriptionData.subscriptionApiFlowResponse = {
                status: req.status,
                typeId: typeID,
              };
            }
          } else {
            console.warn('Warn: not correct request, check parameters');
          }
        } catch (error) {
          const isPartner = $config.tenant?.default?.isPartner;
          if (isPartner) {
            if (
              error.response?.data?.data?.error ===
              'REQUIRED_IDENTITY_FIELD_NOT_FOUND'
            ) {
              return error.response;
            }
            console.error('Error: in subscribing');
          }
          subscriptionData.subscriptionApiFlowResponse = {
            status: error?.response?.status,
            typeId: typeID,
            error: error.response?.data?.data?.error,
          };
          console.error('Error: in subscribing');
        }
      },
      async upgrade(
        typeID: number,
        language: string,
        redirectUrl: string,
        articleId: number,
        purchaseType: string,
        migrateSubId: number,
        subscriptionConsentList: any,
        selectedOptions: any,
      ): Promise<unknown> {
        try {
          if (
            $config.tenant?.default?.mssAutoSubscription &&
            !subscriptionData.isLoggedIn
          ) {
            useNuxtApp().$login.login('', '', '', typeID);
            return;
          }
          if (typeID && language && redirectUrl) {
            // remove message and status from query string
            const url = new URL(redirectUrl);
            url.searchParams.delete('message');
            url.searchParams.delete('status');
            const redirectUrlWithoutQueryString = url.toString();
            let urlQueryParam = redirectUrlWithoutQueryString.includes('?')
              ? redirectUrlWithoutQueryString + '&type=' + typeID
              : redirectUrlWithoutQueryString + '?type=' + typeID;

            if (articleId && purchaseType) {
              urlQueryParam += `&purchaseID=${articleId}&purchaseType=${purchaseType}`;
            }
            const price = subscriptionData.getSubPrice(typeID);
            const req: any = await $api.endPoints.subscribeUser(
              typeID,
              language,
              urlQueryParam,
              price,
              $auth.user.userId,
              subscriptionConsentList,
              migrateSubId,
              selectedOptions,
            );
            if (
              req?.data?.data?.error === 'REQUIRED_IDENTITY_FIELD_NOT_FOUND'
            ) {
              return req.data.data;
            }
            if (req?.data?.options?.length) {
              if (req?.data?.options.length > 1) {
                const selectedSubcription =
                  subscriptionData.subscriptionTypes.find(
                    (sub) => sub.id == typeID,
                  );
                useNuxtApp().$dialogs.openPaymentOptionsPop(
                  req?.data?.options,
                  selectedSubcription,
                );
                return;
              } else {
                subscriptionData.subscribeToMss(req.data.options[0], typeID);
                return;
              }
            }
            if (req?.data?.type == 'redirect' && req?.data?.url) {
              return (window.location.href =
                req.data.url + `?lang=${language}`);
            } else if (req?.data?.type == 'onclick') {
              subscriptionData.subscriptionApiFlowResponse = {
                status: req.status,
                typeId: typeID,
              };
            }
          } else {
            console.warn('Warn: not correct request, check parameters');
          }
        } catch (error: any) {
          const isPartner = $api.tenant?.default?.isPartner;
          if (isPartner) {
            if (
              error.response?.data?.data?.error ===
              'REQUIRED_IDENTITY_FIELD_NOT_FOUND'
            ) {
              return error.response;
            }
            console.error('Error: in subscribing');
          }
          subscriptionData.subscriptionApiFlowResponse = {
            status: error?.response?.status,
            typeId: typeID,
            error: error.response?.data?.data?.error,
          };
          console.error('Error: in subscribing');
        }
      },
      getPaymentOptions: async (
        typeID: number,
        language: string,
        redirectUrl: string,
        articleId: number,
        purchaseType: string,
      ): Promise<unknown> => {
        try {
          if (typeID && language && redirectUrl) {
            // remove message and status from query string
            const url = new URL(redirectUrl);
            url.searchParams.delete('message');
            url.searchParams.delete('status');
            const redirectUrlWithoutQueryString = url.toString();

            let urlQueryParam = redirectUrlWithoutQueryString.includes('?')
              ? redirectUrlWithoutQueryString + '&type=' + typeID
              : redirectUrlWithoutQueryString + '?type=' + typeID;

            if (articleId && purchaseType) {
              urlQueryParam += `&purchaseID=${articleId}&purchaseType=${purchaseType}`;
            }
            const price = subscriptionData.getSubPrice(typeID);
            const req = await $api.endPoints.subscribeUser(
              typeID,
              language,
              urlQueryParam,
              price,
            );
            return req;
          } else {
            console.warn('Warn: not correct request, check parameters');
          }
        } catch (error: any) {
          subscriptionData.subscriptionApiFlowResponse = {
            status: error?.response?.status,
            typeId: typeID,
          };
          console.error('Error: in subscribing');
        }
      },
      getUserSubscription: async (
        inclCancelledSub?: boolean,
      ): Promise<void> => {
        try {
          subscriptionData.isUserSubscriptionLoaded = false;
          let user = $auth.user;
          if (user?.roles && user.roles[0] === 'user') {
            const req: any =
              await $api.endPoints.getUserSubscription(inclCancelledSub);
            subscriptionData.userSubscriptionRaw = req.data;
            subscriptionData.isUserSubscriptionLoaded = true;
            return;
          }
        } catch (error) {
          console.error('Error: user subscriptions');
        }
      },
      isVoucherbookEligible: async (subsTypeId?: string): Promise<void> => {
        try {
          let user = $auth.user as { roles: string[] };
          if (user?.roles && user.roles[0] === 'user') {
            const req = await $api.endPoints.isVoucherbookEligible(subsTypeId);
            return req.data;
          }
        } catch (error) {
          console.error('Error: ', 'user isVoucherbookEligible');
        }
      },
      getSubscriptionTypes: async (): Promise<void> => {
        try {
          const req: any = await $api.endPoints.getSubscriptionTypes();
          if (req) {
            const blockedSubscriptions =
              $store?.state?.geoBlockFlag &&
              $api.tenant?.default?.geoBlock?.blockedSubscriptions;
            if (blockedSubscriptions) {
              subscriptionData.subscriptionTypes = req.data.filter(
                (sub: any) => !blockedSubscriptions.includes(sub.id),
              );
            } else {
              subscriptionData.subscriptionTypes = req.data;
            }
            subscriptionData.subscriptionTypes.forEach(
              (element: SubscriptionType) => {
                subscriptionData.allowedSubTypes.push(element.id);
                if (!element['attributes']) {
                  element['attributes'] = [];
                }
                const foundCategory = element['attributes']?.find(
                  (category) => {
                    return category.key === ('ment_category' as String);
                  },
                );
                if (!foundCategory) {
                  element['attributes'].push({
                    key: 'ment_category',
                    value: '["unkown' + element.id + '"]',
                  });
                }
                element.billingCycleName = $t(priceCycle(element.billingCycle));
                element.durationName =
                  element.billingCycle && $t(element.billingCycle);
                element.priceString = element;
                element.ment_category = element.attributes.find((category) => {
                  return category.key === ('ment_category' as String);
                }).value;
                const bestValueObj = element.attributes.find((category) => {
                  return category.key === ('ment_best_value' as String);
                });
                const sort = element.attributes.find((category) => {
                  return category.key === ('ment_sort' as String);
                });
                const freeDuration = element.attributes?.find(
                  (note: { [key: string]: string }) => {
                    return note.key === `ment_free_duration_${$i18n.locale}`;
                  },
                )?.value;
                element.ment_best_value = bestValueObj
                  ? !!+bestValueObj.value
                  : false;
                element.ment_free_duration = freeDuration ? freeDuration : '';
                element.sort = sort ? +sort.value : 0;
              },
            );
          }
          subscriptionData.subscriptionTypes.sort(
            (a: SubscriptionType, b: SubscriptionType) => {
              return a.sort > b.sort ? 1 : -1;
            },
          );
          subscriptionData.loadingSubTypes = false;
          return;
        } catch (err) {
          subscriptionData.loadingSubTypes = false;
          console.error('get types');
        }
      },
      isSubscribedToAny: (
        subscriptionTypes: SubscriptionType[],
        returnObject: boolean = false,
      ): boolean | Object => {
        if (
          subscriptionTypes &&
          subscriptionTypes.length &&
          (subscriptionData?.userActiveSubscription?.length ||
            subscriptionData.inActiveSubscriptions?.length ||
            subscriptionData.pendingSubscriptions?.length)
        ) {
          const foundSub =
            subscriptionData?.userActiveSubscription?.find(
              (item) =>
                subscriptionTypes.find((sub) => sub.id == item.typeId) ||
                subscriptionTypes.find((sub) =>
                  item.subscriptionType.attributes.find(
                    (x) =>
                      x.key === 'ment_category' &&
                      x.value === sub.ment_category,
                  ),
                ),
            ) ||
            subscriptionData.inActiveSubscriptions?.find((item: any) =>
              subscriptionTypes.find((sub) => sub.id == item.typeId),
            ) ||
            subscriptionData.pendingSubscriptions?.find((item: any) =>
              subscriptionTypes.find((sub) => sub.id == item.typeId),
            );
          const filteredContingent = foundSub?.contingents?.filter(
            (item: any) => item.amount > 0 || item.amount == -1,
          ).length;
          if (filteredContingent !== 0 && foundSub?.contingents?.length) {
            let largest = 0;
            subscriptionTypes.forEach((i) => {
              if (i.contingents && i.contingents.length) {
                i.contingents.forEach((c: any) => {
                  largest = largest < c.amount ? c.amount : largest;
                });
              }
            });
            subscriptionTypes.find((x) => x.id == foundSub.subscriptionType.id);
            const currentSubAmount =
              foundSub.subscriptionType?.contingents?.reduce(
                (acc: any, item: any) =>
                  item.amount > acc ? item.amount : acc,
                0,
              );
            if (currentSubAmount < largest) {
              return null;
            } else {
              return returnObject ? foundSub : !!foundSub;
            }
          }

          return returnObject ? foundSub : !!foundSub;
        } else {
          return false;
        }
      },
      getRelatedMssUserSubscription: (
        subscriptionTypes: SubscriptionType[],
      ): boolean | Object => {
        if (subscriptionTypes && subscriptionTypes.length) {
          const allActiveSubs =
            subscriptionData.getRelatedSubscriptionMSS(subscriptionTypes);
          const foundActiveSub = allActiveSubs?.filter((userSub) => {
            return subscriptionTypes?.some((subType) => {
              return userSub.typeId == subType.id;
            });
          });
          return { foundActiveSub: foundActiveSub?.[0], allActiveSubs };
        } else {
          return false;
        }
      },
      getCategory: (item: SubscriptionType): unknown => {
        const category: any =
          item?.['attributes'].find((category) => {
            return category.key === ('ment_category' as String);
          })?.value ||
          item?.subscriptionType?.['attributes'].find((category) => {
            return category.key === ('ment_category' as String);
          })?.value;
        if (!category) {
          return [];
        }
        try {
          let parseCategory = JSON.parse(category);
          parseCategory.sort();
          return parseCategory;
        } catch (error) {
          console.error('error in parsing category in susbcription plugin');
        }
      },
      getDefaultSubscription: (
        subTypes: SubscriptionType[],
      ): SubscriptionType => {
        return (
          subTypes?.find((el) => {
            if (!Array.isArray(el.attributes)) {
              return false;
            }
            const mentDefaultAttribute = el.attributes?.find(
              (attr) => attr.key === 'ment_default',
            );
            return mentDefaultAttribute?.value !== '0';
          }) ||
          (subTypes && subTypes[0])
        );
      },
      async getMarketingSubTypes(typeIdsString: string) {
        try {
          const typeIds = JSON.parse(typeIdsString);
          if (typeIds && Array.isArray(typeIds)) {
            const subtypesResponses = await Promise.allSettled(
              typeIds.map((id) =>
                $api.endPoints.getEnabledSubscriptionTypeById(id),
              ),
            );
            const formatedSubtypes = subtypesResponses.reduce<
              SubscriptionType[]
            >((acc, i) => {
              if (i.status === 'fulfilled') {
                const sub = i.value;
                sub.billingCycleName = $t(priceCycle(sub.billingCycle));
                sub.durationName = $t(sub.billingCycle);
                sub.priceString = sub;
                sub.ment_category = sub.attributes.find((category) => {
                  return category.key === ('ment_category' as String);
                }).value;
                acc.push(sub);
              }
              return acc;
            }, []);
            return formatedSubtypes;
          }
        } catch (error) {
          return [];
        }
      },
      getClubs: (
        arr: string | string[],
        filtered?: boolean,
      ): SubscriptionType[] | unknown => {
        try {
          let clubsIdsArr = !Array.isArray(arr) ? JSON.parse(arr) : arr;
          clubsIdsArr =
            typeof clubsIdsArr !== 'object' ? [clubsIdsArr] : clubsIdsArr;

          let allClubs = [...clubsIdsArr]
            .map((el: any) => subscriptionData.subTypesMap[el])
            .filter((e) => e != undefined);

          const categoryNames = allClubs
            .map((e: SubscriptionType) => subscriptionData.getCategory(e))
            .map((i: any) => i.join(','));
          const uniq = [...new Set(categoryNames)];

          let sorter = [] as any;
          try {
            sorter =
              $globals?.paymentMetaData?.groupsOrder &&
              JSON.parse($globals?.paymentMetaData?.groupsOrder);
          } catch (error) {
            // just  skip
          }
          if (sorter && sorter.length) {
            uniq.sort((a, b) => {
              const aIndex = sorter.indexOf(a);
              const bIndex = sorter.indexOf(b);
              if (aIndex == -1) {
                return -1;
              }
              if (bIndex == -1) {
                return -1;
              }
              return aIndex > bIndex ? 1 : -1;
            });
          }
          let groups = uniq.map((i: unknown) => {
            return subscriptionData.groupedByCategoryAll[i as string];
          });
          if (subscriptionData.userActiveSubscription?.length && filtered) {
            const userSubscriptions: Map<string, string> = (
              subscriptionData.userActiveSubscription as ActiveSubscriptions[]
            ).reduce((map, res) => {
              map.set(res.subscriptionType.id, res.subscriptionType.id);
              return map;
            }, new Map());
            groups = groups.filter((parent) => {
              for (const item of parent) {
                if (userSubscriptions.has(item.id)) {
                  return false;
                }
              }
              return true;
            });
          }
          return groups;
        } catch (error) {
          return [];
        }
      },
      getSubPrice: (typeID: number) => {
        const selectedSubcription: any =
          subscriptionData.subscriptionTypes.find(
            (sub: any) => sub.id == typeID,
          );
        let price =
          selectedSubcription?.subscriptionTypePrice?.amount +
          selectedSubcription?.subscriptionTypePrice?.currency;
        return price;
      },
      getSubInfoById: (typeID: number) => {
        const selectedSubcription: any =
          subscriptionData.subscriptionTypes.find(
            (sub: any) => sub.id == typeID,
          );
        return selectedSubcription;
      },
      subscriptionApiFlow: async (
        opts: {
          paymentOption: any;
          typeId: number;
          body: {};
          upgradeTo: any;
        },
        selectedOptions?: any[],
      ): Promise<void> => {
        const { paymentOption, typeId, body, upgradeTo } = opts;
        try {
          let data;
          if (upgradeTo) {
            data = await $api.endPoints.apiFlow({
              paymentOption,
              type: 'any',
              body,
            });
          } else {
            data = await $api.endPoints.subscriptionApiFlow(
              paymentOption,
              body,
            );
          }
          const response = data?.response;
          if (response && typeId) {
            if (response?.status == 302 && response?.url) {
              useNuxtApp().$dialogs.loader = false;
              if (
                $globals.enabledFeatures
                  .enablePaymentInWebIframeWithoutRedirection
              ) {
                useNuxtApp().$dialogs.openPaymentWebFlowInIframe(response?.url);
              } else {
                window.location.href = response?.url;
              }
            } else {
              subscriptionData.subscriptionApiFlowResponse = {
                ...response,
                typeId,
              };
              if (selectedOptions?.length) {
                $api.endPoints.updatePartnerMetadataSubscriptionAttribute(
                  response.subscriptionId,
                  selectedOptions.map((option) => ({ id: option.id })),
                );
              }
            }
          }
          subscriptionData.nextApiFlowStep = data?.nextApiFlowStep;
        } catch (error: any) {
          const res = error?.response;
          if (res?.status == '401') {
            useNuxtApp().$login.login(null, null, null, null, typeId);
            useNuxtApp().$dialogs.paymentOptionsDialog = false;
            return;
          }
          const i18Message = res?.data?.details?.i18Message;
          const errorMessage = res?.data.error;
          subscriptionData.subscriptionApiFlowResponse = {
            status: res?.status,
            typeId,
            i18Message,
            error: errorMessage,
          };
        }
      },
      subscribeToMss: async (
        paymentOption: any,
        typeId: number,
        selectedOptions?: any[],
      ) => {
        switch (paymentOption.flow) {
          case paymentFlow.WEB:
            useNuxtApp().$dialogs.loader = false;
            if (
              $globals.enabledFeatures
                .enablePaymentInWebIframeWithoutRedirection
            ) {
              useNuxtApp().$dialogs.openPaymentWebFlowInIframe(
                paymentOption.redirectionUrl,
              );
            } else {
              window.location.href = paymentOption.redirectionUrl;
            }
            break;
          case paymentFlow.API:
            let upgradeTo = sessionStorage.getItem('upgradeTo');
            if (upgradeTo) {
              upgradeTo = JSON.parse(upgradeTo);
            }
            await subscriptionData.subscriptionApiFlow(
              {
                paymentOption,
                typeId,
                body: {},
                upgradeTo,
              },
              selectedOptions,
            );
            break;
        }
      },
      isUserSubscribedToArticle: (
        offerAttributes: any[],
        contentType: string,
      ) => {
        const userSubscriptions =
          (subscriptionData.isUserSubsReady &&
            subscriptionData.userActiveSubscription) ||
          [];
        let isSubscribed = false;
        if (contentType == 'music') {
          isSubscribed = userSubscriptions.some((sub: any) => {
            return (
              offerAttributes?.indexOf(sub.typeId) >= 0 &&
              sub.subscriptionType?.contingents &&
              !sub.subscriptionType?.contingents.length
            );
          });
        } else {
          isSubscribed = userSubscriptions.some((sub: any) => {
            return offerAttributes?.indexOf(sub.typeId) >= 0;
          });
        }

        return isSubscribed;
      },
      articleHasOfferAttributes: (article: any) => {
        if (article?.prices?.length) {
          const offerAttributes = [] as any;
          article.prices.forEach((item: any) => {
            item.offerAttributes?.map(({ key, value }: any) => {
              if (key == 'SubscriptionTypeId') {
                offerAttributes.push(parseInt(value));
              }
            });
          });
          return offerAttributes;
        }
      },
      setPartnerSubscriptions({ sub, selectedSubs }) {
        subscriptionData.selectedPartnerSubscriptions = selectedSubs || [];
        subscriptionData.partnerSubscriptionsPlans = [sub];
      },
      fireTrackingEvent(typeId: number) {
        const price = subscriptionData.getSubPrice(typeId);
        const subDetails = subscriptionData.getSubInfoById(typeId);
        
        useNuxtApp().$customGtm.track({
            ET: 'Event',
            EN: 'subscription_start',
            AT: 'subscribe',
            EC: 'Subscription Process',
            SN: subDetails?.name,
            SI: typeId.toString(),
            PA: price,
          });
      },
    });

    // watch section //
    if (import.meta.client) {
      watch(
        () => $auth.user,
        async (v) => {
          if (v) {
            await Promise.all([
              subscriptionData.getSubscriptionTypes(),
              subscriptionData.getUserSubscription(true),
              subscriptionData.isFreeTrialEligible(),
              subscriptionData.getUserWallet(),
            ]);
          }
        },
        {
          immediate: true,
        },
      );

      // watch(
      //   () => subscriptionData.subsIsReady,
      //   async (v) => {
      //     if (v) {
      //       await subscriptionData.isFreeTrialEligible();
      //       await subscriptionData.getUserWallet();
      //     }
      //   },
      //   {
      //     immediate: true,
      //   },
      // );

      watch(
        () => subscriptionData.userSubscriptionRaw,
        async (v) => {
          if (v) {
            subscriptionData.subscriptionTypes.forEach((item: any) => {
              item.isUserSubscribed = subscriptionData.userSubscriptionRaw.some(
                (userSub: any) => userSub.active && userSub.typeId === item.id,
              );
            });
          }
        },
        {
          immediate: true,
        },
      );
    }

    return {
      provide: {
        subscription: subscriptionData,
      },
    };
  },
});
export enum paymentFlow {
  API = 'API',
  WEB = 'WEB',
}
export enum upgradeSubscriptionFlow {
  Upgrade = 'upgrade',
  Downgrade = 'downgrade',
  Change = 'change',
}
